/**
 * Resolve prefixed manifest keys against the user's company.
 *
 * Manifest fields and `displayOptions` accept three external prefixes that read
 * from `me.company.*` (Apollo cache via `useAuthStore`) instead of form values:
 *
 *  - `feature.<key>`  → `company.feature[key]`               (boolean flags)
 *  - `account.<key>`  → `company.featureData[mapped]`        (typed values, via ACCOUNT_KEY_MAP)
 *  - `system.<key>`   → runtime/Intl resolver                (e.g. `system.timezone`)
 *
 * Single source of truth used by:
 *  - `useDisplayOptions` for prefixed visibility gates
 *  - `account-defaults.ts` for `defaultFrom` value sourcing
 */
import type { CompanyInfo, CompanyFeatureData } from '@scio3/shared-types';

/** Maps `account.<key>` prefix to the corresponding CompanyFeatureData field. */
export const ACCOUNT_KEY_MAP: Record<string, keyof CompanyFeatureData> = {
  'account.temperature': 'scaleTemperature',
  'account.dateFormat': 'dateFormat',
  'account.timeFormat': 'timeFormat',
  'account.numberFormat': 'numberFormat',
  'account.windSpeed': 'windSpeedUnit',
};

/** Recognized external-source prefixes. */
export const EXTERNAL_PREFIXES = ['feature.', 'account.', 'system.', 'payment.'] as const;

/** True when a key starts with one of the external-source prefixes. */
export function isExternalKey(key: string): boolean {
  return EXTERNAL_PREFIXES.some((p) => key.startsWith(p));
}

function resolveSystemKey(key: string): unknown {
  if (key === 'system.timezone') {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/**
 * Resolve an external-source key against the company. Returns `undefined` for
 * unknown prefixes, unmapped sub-keys, or missing company data — callers treat
 * this as "no value" (visibility gates → false-y, defaultFrom → fall back to
 * static `default`).
 */
export function resolveCompanyKey(key: string, company: CompanyInfo | null | undefined): unknown {
  if (key.startsWith('system.')) {
    return resolveSystemKey(key);
  }

  if (!company) return undefined;

  if (key.startsWith('feature.')) {
    const subKey = key.slice('feature.'.length);
    if (!subKey) return undefined;
    return (company.feature as unknown as Record<string, unknown> | undefined)?.[subKey];
  }

  if (key.startsWith('account.')) {
    const featureKey = ACCOUNT_KEY_MAP[key];
    if (!featureKey) return undefined;
    return company.featureData?.[featureKey];
  }

  if (key.startsWith('payment.')) {
    const subKey = key.slice('payment.'.length);
    if (!subKey) return undefined;
    return (company.payment as unknown as Record<string, unknown> | undefined)?.[subKey];
  }

  return undefined;
}
